import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './style/data-table.css';
import LoginForm from './HomeMgmt/LoginForm';
import ClientDetailsForm from './ClientMgmt/ClientDetailsForm';
import BulkClientDetailsForm from './ClientMgmt/BulkClientDetailsForm';
import LoanDisbursement from './LoanMgmt/LoanDisbursement';
import LoanProductManagement from './LoanMgmt/LoanProductManagement';
import LoanProductPreview from './LoanMgmt/LoanProductPreview';
import LoanRepayment from './LoanMgmt/LoanRepayment';
import DisbursementClientQueue from './LoanMgmt/DisbursementClientQueue';
import DisbursementClientHistory from './LoanMgmt/DisbursementClientHistory';
import RepaymentApprovalRequests from './LoanMgmt/RepaymentApprovalRequests';
import RepaymentReport from './Reports/RepaymentReport';
import AdvanceRepaymentReport from './Reports/AdvanceRepaymentReport';
import OperationsReport from './Reports/OperationsReport';
import OdReport from './Reports/OdReport';
import CenterMgmt from './CenterMgmt';
import LoanRepaymentList from './LoanMgmt/LoanRepaymentList';
import ClientManagement from './ClientMgmt';
import Dashboard from './Dashboard';
import EmployeeManagement from './EmployeeMgmt';
import UserManagement from './UserMgmt';
import { AuthProvider, useAuth } from './AuthContext';
import { PopupProvider } from './Common/PopupProvider';
import InlineFormValidation from './Common/InlineFormValidation';
import { API_BRANCHES } from './Config/ApiUrlConstant';
import { apiJson } from './utils/apiClient';

const appRoutes = [
  { to: '/dashboard', label: 'Dashboard', moduleKey: 'dashboard', element: <Dashboard /> },
  { to: '/centers', label: 'Center Management', moduleKey: 'centers', element: <CenterMgmt /> },
  { to: '/employees', label: 'Employee Management', moduleKey: 'employees', element: <EmployeeManagement /> },
  { to: '/clients', label: 'Client Management', moduleKey: 'clients', element: <ClientManagement /> },
  { to: '/client-details', label: 'Client Details', moduleKey: 'clients', element: <ClientDetailsForm />, hidden: true },
  { to: '/client-details/:clientId', label: 'Client Details Edit', moduleKey: 'clients', element: <ClientDetailsForm />, hidden: true },
  { to: '/client-details-bulk', label: 'Bulk Client Creation', moduleKey: 'clients', element: <BulkClientDetailsForm /> },
  { to: '/loan-disbursement', label: 'Single Loan Disbursement', moduleKey: 'disbursement', element: <LoanDisbursement initialMode="single" /> },
  { to: '/loan-products', label: 'Loan Products', moduleKey: 'loan_management', element: <LoanProductManagement /> },
  { to: '/loan-product-preview', label: 'EMI Preview', moduleKey: 'loan_management', element: <LoanProductPreview /> },
  { to: '/loan-disbursement-multiple', label: 'Multiple Loan Disbursement', moduleKey: 'disbursement', element: <LoanDisbursement initialMode="multiple" /> },
  { to: '/add-client-for-disbursement', label: 'Add Client For Disbursement', moduleKey: 'disbursement_queue', element: <DisbursementClientQueue /> },
  { to: '/disbursement-client-history', label: 'Disbursement Client History', moduleKey: 'disbursement_history', element: <DisbursementClientHistory /> },
  { to: '/loan-repayment-list', label: 'Repayment Management', moduleKey: 'repayments', element: <LoanRepaymentList /> },
  { to: '/loan-repayment', label: 'Single Loan Repayment', moduleKey: 'repayments', element: <LoanRepayment /> },
  { to: '/repayment-requests', label: 'Repayment Requests', moduleKey: 'repayments', element: <RepaymentApprovalRequests /> },
  { to: '/repayment-report', label: 'Repayment Report', moduleKey: 'repayment_report', element: <RepaymentReport /> },
  { to: '/advance-repayment-report', label: 'Advance Repayment Report', moduleKey: 'advance_repayment_report', element: <AdvanceRepaymentReport /> },
  { to: '/operations-report', label: 'Operations Report', moduleKey: 'operations_report', element: <OperationsReport /> },
  { to: '/od-report', label: 'OD Report', moduleKey: 'od_report', element: <OdReport /> },
  { to: '/users', label: 'User Management', moduleKey: 'user_management', element: <UserManagement /> },
];

const menuItems = [
  { type: 'link', to: '/dashboard', label: 'Dashboard', moduleKey: 'dashboard' },
  { type: 'link', to: '/centers', label: 'Center Management', moduleKey: 'centers' },
  { type: 'link', to: '/employees', label: 'Employee Management', moduleKey: 'employees' },
  {
    type: 'group',
    label: 'Loan Management',
    moduleKey: 'loan_management',
    children: [
      { to: '/loan-products', label: 'Loan Products', moduleKey: 'loan_management' },
      { to: '/loan-product-preview', label: 'EMI Preview', moduleKey: 'loan_management' },
    ],
  },
  {
    type: 'group',
    label: 'Client Management',
    moduleKey: 'clients',
    children: [
      { to: '/clients', label: 'Client List', moduleKey: 'clients' },
      { to: '/client-details', label: 'Single Client', moduleKey: 'clients' },
      { to: '/client-details-bulk', label: 'Bulk Clients', moduleKey: 'clients' },
    ],
  },
  {
    type: 'group',
    label: 'Disbursement Management',
    moduleKey: 'disbursement',
    children: [
      { to: '/loan-disbursement', label: 'Single Disbursement', moduleKey: 'disbursement' },
      { to: '/loan-disbursement-multiple', label: 'Multiple Disbursements', moduleKey: 'disbursement' },
      { to: '/add-client-for-disbursement', label: 'Add Client For Disbursement', moduleKey: 'disbursement_queue' },
      { to: '/disbursement-client-history', label: 'Disbursement History', moduleKey: 'disbursement_history' },
    ],
  },
  {
    type: 'group',
    label: 'Repayment Management',
    moduleKey: 'repayments',
    children: [
      { to: '/loan-repayment-list', label: 'Bulk Repayment', moduleKey: 'repayments' },
      { to: '/loan-repayment', label: 'Single Repayment', moduleKey: 'repayments' },
      { to: '/repayment-requests', label: 'Repayment Requests', moduleKey: 'repayments' },
    ],
  },
  {
    type: 'group',
    label: 'Reports',
    moduleKey: 'reports',
    children: [
      { to: '/repayment-report', label: 'Repayment Report', moduleKey: 'repayment_report' },
      { to: '/advance-repayment-report', label: 'Advance Repayment Report', moduleKey: 'advance_repayment_report' },
      { to: '/operations-report', label: 'Operations Report', moduleKey: 'operations_report' },
      { to: '/od-report', label: 'OD Report', moduleKey: 'od_report' },
    ],
  },
  { type: 'link', to: '/users', label: 'User Management', moduleKey: 'user_management' },
];

function App() {
  return (
    <Router>
      <AuthProvider>
        <PopupProvider>
          <InlineFormValidation />
          <AppContent />
        </PopupProvider>
      </AuthProvider>
    </Router>
  );
}

function ProtectedRoute({ moduleKey, children }) {
  const { isAuthenticated, canAccess } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!canAccess(moduleKey, 'view')) {
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function AppContent() {
  const { isAuthenticated, logout, canAccess, user, selectedBranchId, selectBranch } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openMenuGroups, setOpenMenuGroups] = useState({});
  const [branches, setBranches] = useState([]);
  const isGlobalRole = ['admin', 'super_admin'].includes(user?.role_key);
  useEffect(() => { if (isAuthenticated && isGlobalRole) void apiJson(API_BRANCHES, { method: 'POST', body: JSON.stringify({}) }).then(({ data }) => setBranches(data?.data || [])); }, [isAuthenticated, isGlobalRole]);

  const visibleMenuItems = useMemo(
    () => menuItems
      .map((item) => {
        if (item.type === 'group') {
          if (item.moduleKey !== 'reports' && !canAccess(item.moduleKey, 'view')) {
            return null;
          }

          return {
            ...item,
            children: item.children.filter((child) => canAccess(child.moduleKey, 'view')),
          };
        }

        return canAccess(item.moduleKey, 'view') ? item : null;
      })
      .filter((item) => item && (item.type !== 'group' || item.children.length > 0)),
    [canAccess]
  );

  const defaultProtectedRoute = useMemo(() => {
    const firstItem = visibleMenuItems[0];

    if (!firstItem) {
      return '/dashboard';
    }

    if (firstItem.type === 'group') {
      return firstItem.children[0]?.to || '/dashboard';
    }

    return firstItem.to;
  }, [visibleMenuItems]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="app-container">
        {isAuthenticated && (
          <>
            <div className="mobile-header">
              <button className="hamburger" onClick={toggleSidebar} aria-label="Toggle menu">
                Menu
              </button>
              <h2>Dashboard</h2>
            </div>

            {sidebarOpen ? (
              <button className="sidebar-backdrop" onClick={closeSidebar} aria-label="Close menu" />
            ) : null}

            <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
              <div className="logo">
                <h2>Menu</h2>
                {isGlobalRole ? <select className="form-control mb-2" value={selectedBranchId} onChange={(event) => selectBranch(event.target.value)}><option value="">All Branches</option>{branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.branch_name}</option>)}</select> : null}
                {user?.employee_branch_name ? <p className="assigned-branch">Assigned branch: {user.employee_branch_name}</p> : null}
              </div>
              <ul className="menu">
                {visibleMenuItems.map((item) => (
                  item.type === 'group' ? (
                    <li key={item.label}>
                      {(() => {
                        const isActiveGroup = item.children.some((child) => child.to === location.pathname);
                        const isGroupOpen = openMenuGroups[item.moduleKey] ?? isActiveGroup;

                        return <>
                      <button
                        type="button"
                        className="menu-group-label menu-group-toggle"
                        onClick={() => setOpenMenuGroups((current) => ({
                          ...current,
                          [item.moduleKey]: !(current[item.moduleKey] ?? isActiveGroup),
                        }))}
                        aria-expanded={isGroupOpen}
                      >
                        {item.label}
                      </button>
                      {isGroupOpen ? (
                        <div className="menu-group-links">
                          {item.children.map((child) => (
                            <Link key={child.to} to={child.to} onClick={closeSidebar} className="menu-sublink">
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      ) : null}
                        </>;
                      })()}
                    </li>
                  ) : (
                    <li key={item.to}>
                      <Link to={item.to} onClick={closeSidebar}>{item.label}</Link>
                    </li>
                  )
                ))}
                <li><button onClick={() => { logout(); closeSidebar(); }} className="logout-button">Logout</button></li>
              </ul>
            </div>
          </>
        )}

        <div className={`content ${isAuthenticated ? '' : 'content-login'}`}>
          <Routes>
            <Route path="/login" element={!isAuthenticated ? <LoginForm /> : <Navigate to={defaultProtectedRoute} />} />
            {appRoutes.map((route) => (
              <Route
                key={route.to}
                path={route.to}
                element={<ProtectedRoute moduleKey={route.moduleKey}>{route.element}</ProtectedRoute>}
              />
            ))}
            <Route path="/" element={<Navigate to={isAuthenticated ? defaultProtectedRoute : '/login'} />} />
          </Routes>
        </div>
    </div>
  );
}

export default App;
